import { useMemo } from 'react'
import { Navigate, NavLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  SETTINGS_GROUPS,
  DEFAULT_SETTINGS_SLUG,
  MANAGER_DEFAULT_SETTINGS_SLUG,
} from '@/config/settingsNav'
import { DOCUMENT_TYPE, type TemplateDocumentType } from '@/lib/documentType'
import { useAppSelector } from '@/store/hooks'
import { UsersPanel } from '@/components/settings/users/UsersPanel'
import { CategoriesPanel } from '@/components/settings/services/CategoriesPanel'
import { ItemsPanel } from '@/components/settings/services/ItemsPanel'
import { TaxRatesPanel } from '@/components/settings/services/TaxRatesPanel'
import { OptionalFeesPanel } from '@/components/settings/services/OptionalFeesPanel'
import { WorkingDaysHoursPanel } from '@/components/settings/calendar/WorkingDaysHoursPanel'
import { HolidaysPanel } from '@/components/settings/calendar/HolidaysPanel'
import { DocumentNumberingPanel } from '@/components/settings/documents/DocumentNumberingPanel'
import { TemplatePanel } from '@/components/settings/templates/TemplatePanel'
import { ReusableDocumentsPanel } from '@/components/settings/library/ReusableDocumentsPanel'
import { GeneralInformationPanel } from '@/components/settings/company/GeneralInformationPanel'
import { NotificationEventsPanel } from '@/components/settings/notifications/NotificationEventsPanel'
import type { SettingsNavGroup, SettingsNavItem } from '@/config/settingsNav'

function renderTemplatePanel(
  slug: string,
  docType: TemplateDocumentType,
  title: string
) {
  // All document-template tabs share the same restyled shell; key by slug/doc
  // type so switching tabs remounts cleanly and does not carry editor/list
  // state across Quote / Invoice / Credit note / Contract / Report.
  return (
    <TemplatePanel key={`${slug}-${docType}`} docType={docType} title={title} />
  )
}

function renderPanel(slug: string, t: (key: string) => string) {
  switch (slug) {
    case 'users':
      return <UsersPanel />
    case 'service-categories':
      return <CategoriesPanel />
    case 'service-items':
      return <ItemsPanel />
    case 'tax-rates':
      return <TaxRatesPanel />
    case 'optional-fees':
      return <OptionalFeesPanel />
    case 'working-days-hours':
      return <WorkingDaysHoursPanel />
    case 'public-holidays':
      return <HolidaysPanel />
    case 'document-numbering':
      return <DocumentNumberingPanel />
    case 'reusable-documents':
      return <ReusableDocumentsPanel />
    case 'quote-template':
      return renderTemplatePanel(
        slug,
        DOCUMENT_TYPE.QUOTE,
        t('templates.quote.title')
      )
    case 'invoice-template':
      return renderTemplatePanel(
        slug,
        DOCUMENT_TYPE.INVOICE,
        t('templates.invoice.title')
      )
    case 'credit-note-template':
      return renderTemplatePanel(
        slug,
        DOCUMENT_TYPE.CREDIT_NOTE,
        t('templates.creditNote.title')
      )
    case 'contract-template':
      return renderTemplatePanel(
        slug,
        DOCUMENT_TYPE.CONTRACT,
        t('templates.contract.title')
      )
    case 'report-template':
      return renderTemplatePanel(
        slug,
        DOCUMENT_TYPE.INTERVENTION_REPORT,
        t('templates.report.title')
      )
    case 'general-information':
      return <GeneralInformationPanel />
    case 'notification-events':
      return <NotificationEventsPanel />
    default:
      return null
  }
}

// Settings hub (2.11.x + 2.12.x registry). The active sub-page is selected via
// the :section route param. Route gated by settings.view. Each top-level tab
// only renders items the current role is allowed to access.
export default function SettingsPage() {
  const { t } = useTranslation('settings')
  const { section } = useParams<{ section?: string }>()
  const viewerRole = useAppSelector(s => s.auth.user?.role)
  const isAdmin = viewerRole === 'Admin'
  const canManageUsers = viewerRole === 'Admin' || viewerRole === 'Manager'

  const visibleGroups = useMemo<SettingsNavGroup[]>(
    () =>
      SETTINGS_GROUPS.map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (item.adminOnly && !isAdmin) return false
          if (item.userManagementOnly && !canManageUsers) return false
          return true
        }),
      })).filter(group => group.items.length > 0),
    [isAdmin, canManageUsers]
  )

  const visibleSlugs = visibleGroups.flatMap(g => g.items.map(i => i.slug))
  const activeGroup = visibleGroups.find(group =>
    group.items.some(item => item.slug === section)
  )
  const defaultSlug = visibleSlugs.includes(DEFAULT_SETTINGS_SLUG)
    ? DEFAULT_SETTINGS_SLUG
    : visibleSlugs.includes(MANAGER_DEFAULT_SETTINGS_SLUG)
      ? MANAGER_DEFAULT_SETTINGS_SLUG
      : (visibleSlugs[0] ?? DEFAULT_SETTINGS_SLUG)

  const groupDefaultSlug = (group: SettingsNavGroup) =>
    group.items[0]?.slug ?? defaultSlug

  const subNavLinkClassName = ({ isActive }: { isActive: boolean }): string =>
    cn(
      'relative whitespace-nowrap px-3 py-3 text-sm font-medium leading-[1.4] transition-colors',
      isActive ? 'text-[#3D434E]' : 'text-muted-foreground hover:text-[#3D434E]'
    )

  const renderSubNavLink = (item: SettingsNavItem) => (
    <NavLink
      key={item.slug}
      to={`/settings/${item.slug}`}
      className={subNavLinkClassName}
    >
      {({ isActive }) => (
        <>
          {item.label}
          <span
            className={cn(
              'absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-[#FF5A1F] transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0'
            )}
          />
        </>
      )}
    </NavLink>
  )

  if (!section) return <Navigate to={`/settings/${defaultSlug}`} replace />
  if (section === 'company' || section === 'branding') {
    return (
      <Navigate
        to={`/settings/${isAdmin ? 'general-information' : defaultSlug}`}
        replace
      />
    )
  }
  if (section === 'working-days' || section === 'working-hours')
    return (
      <Navigate
        to={`/settings/${isAdmin ? 'working-days-hours' : defaultSlug}`}
        replace
      />
    )
  if (section === 'document-library' || section === 'attachment-rules')
    return (
      <Navigate
        to={`/settings/${isAdmin ? 'reusable-documents' : defaultSlug}`}
        replace
      />
    )
  if (section === 'quote-settings' || section === 'invoice-settings')
    return <Navigate to='/settings/document-numbering' replace />
  if (!visibleSlugs.includes(section))
    return <Navigate to={`/settings/${defaultSlug}`} replace />

  return (
    <div className='space-y-5'>
      <div className='overflow-hidden rounded-2xl border border-[#e9edf5] bg-white shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14),0_4px_10px_-6px_rgba(38,45,66,0.08)]'>
        <nav className='overflow-x-auto border-b border-[#eef2f6] px-3 pt-2 pb-2'>
          <div className='flex min-w-max items-center gap-2'>
            {visibleGroups.map(group => (
              <NavLink
                key={group.key}
                to={`/settings/${groupDefaultSlug(group)}`}
                className={cn(
                  'rounded-xl px-3.5 py-2 text-sm font-medium leading-[1.4] transition-colors',
                  group === activeGroup
                    ? 'bg-[#FF5A1F] text-white shadow-sm'
                    : 'text-[#3D434E] hover:bg-[#fff3ed]'
                )}
              >
                {group.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {activeGroup ? (
          <nav className='overflow-x-auto border-b border-[#eef2f6] px-3'>
            <div className='flex min-w-max items-center gap-1'>
              {activeGroup.items.map(renderSubNavLink)}
            </div>
          </nav>
        ) : null}
      </div>

      <div className='min-w-0'>{renderPanel(section, t)}</div>
    </div>
  )
}
